import gql from 'graphql-tag'

// Define fragments available to other documents
const fragmentsArray = [
  gql`
    fragment BaseUser on User {
      id
      nameFirst
      nameLast
      fullName
      phone
      email
      createdAt
      isMember
      referralUrl
      lastAppointmentBookedAt
      appointmentCount
    }
  `,
  gql`
    fragment AppointmentServiceAttributes on AppointmentService {
      id
      startDateTime
      appointmentId
      appointment {
        ownerId
      }
      price
      tipRate
      willTipInCash
      duration
      # Provider identity — used for per-provider survey consolidation
      providerId
      service {
        id
        serviceType {
          id
          providerTitle
        }
      }
      baseService {
        id
        name
      }
      addOnServices {
        id
        addOnName
      }
      provider {
        id
        fullName
        nameLast
        nameFirst
        photoUrls
      }
    }
  `,
  gql`
    fragment MarketFields on Market {
      id
      name
      shortName
      isEnabled
      timezone
      cancellationPolicy
      serviceTax
      productTax
      tip
      handlingFee
      addressCompletionOptions
      priority
      minimumAdvanceBookingMinutes
      cutoffTime
      resumeTime
      latitude
      longitude
      addressCompletionOptions
      firstAppointmentTime
      lastAppointmentTime
      priceDisplay
      adjustableTipRate
      fullyAdjustableTipRate
      travelTimeCalculationOptions
    }
  `
]

const FRAGMENTS = fragmentsArray.reduce((accumulator, fragment) => {
  // Assuming the 'fragment' variable is the AST returned by gql and it has the expected structure.
  const fragmentName = fragment.definitions[0].name.value
  if (!fragmentName) {
    throw new Error('Missing fragment name')
  }
  if (fragment.definitions[0].kind !== 'FragmentDefinition') {
    throw new Error('Must define a fragment')
  }
  accumulator[fragmentName] = fragment // Key the fragment by its name
  return accumulator
}, {})

export const USER_FACET_SEARCH = gql`
  query ($where: SequelizeJSON, $order: String!, $limit: Int!, $offset: Int!) {
    users(where: $where, order: $order, limit: $limit, offset: $offset) {
      ...BaseUser
    }
  }
  ${FRAGMENTS.BaseUser}
`

export const LOAD_USER = gql`
  query ($where: SequelizeJSON) {
    users(where: $where) {
      ...BaseUser
    }
  }
  ${FRAGMENTS.BaseUser}
`

export const UPDATE_USER = gql`
  mutation ($id: String!, $user: UserInput!) {
    updateUser(id: $id, user: $user) {
      ...BaseUser
    }
  }
  ${FRAGMENTS.BaseUser}
`

export const FIND_CLIENT = gql`
  query ($where: SequelizeJSON) {
    users(where: $where) {
      ...BaseUser
      createdAt
      discount
      referralUrl
      shouldAutoCloseAppointments
      stripeCustomerId
    }
  }
  ${FRAGMENTS.BaseUser}
`

export const CLIENT_SEARCH = gql`
  query ($where: SequelizeJSON, $appointmentsWhere: SequelizeJSON) {
    users(where: $where, order: "fullName", limit: 50) {
      ...BaseUser
      addresses {
        id
        marketId
        zoneId
        street
        apartment
        city
        state
        zip
        neighborhood
      }
      appointments(
        limit: 1
        order: "actualStartDateTime"
        where: $appointmentsWhere
      ) {
        actualStartDateTime
        address {
          market {
            timezone
          }
        }
      }
    }
  }
  ${FRAGMENTS.BaseUser}
`

export const CREATE_TRANSACTION = gql`
  mutation ($transaction: TransactionInput!) {
    createTransaction(transaction: $transaction) {
      id
    }
  }
`

export const USER_API_REQUESTS = gql`
  query ($userId: String!, $offset: Int!, $limit: Int!) {
    user(id: $userId) {
      apiRequests(limit: $limit, offset: $offset, order: "reverse:createdAt") {
        createdAt
        statusCode
        method
        originalUrl
        clientApplication {
          description
        }
      }
    }
  }
`

export const ME = gql`
  query {
    me {
      id
      ...BaseUser
      isPhoneValid
      isPhoneSmsCapable
      isInServiceArea
      referralCode
      credits
      marketingId
      receiveMarketingEmails
      hasSquad
      photoUrls
      tier
      updatedAt
      registrationClientApplicationId
      defaultPaymentMethodId
      createdAt
      appointments(
        where: { isCanceled: false }
        order: "reverse:createdAt"
        limit: 50
      ) {
        id
        actualStartDateTime
        actualEndDateTime
        isCanceled
        totalPrice
        finalPriceWithTaxAndTip
        taxAmount
        tipAmount
        creditsApplied
        handlingFee
        notes
        address {
          id
          type
          street
          apartment
          city
          state
          zip
          pets
          notes
          name
          market {
            id
            timezone
            serviceTax
            ...MarketFields
          }
        }
        appointmentServices {
          id
          duration
          price
          customPrice
          baseService {
            id
            name
            isInformational
            isAddOn
            serviceType {
              id
              name
            }
          }
          addOnServices {
            id
            name
            price
            description
          }
          provider {
            photoUrls
            nameFirst
            nameLast
          }
        }
      }
      addresses(order: "reverse:createdAt") {
        id
        street
        apartment
        city
        state
        zip
        notes
        type
        pets
        marketId
        latitude
        longitude
        name
      }
      paymentMethods {
        id
        brand
        type
        number
        fullName
        expirationMonth
        expirationYear
      }
      memberships(
        where: { isActive: true }
        order: "reverse:createdAt"
        limit: 1
      ) {
        id
        isActive
        isPaused
        pausedUntil
        lastRenewedAt
        renewalPeriod
        createdAt
        product {
          id
          name
        }
      }
      userCredits {
        id
        amount
        isMembership
        isPaid
        isExpired
        isFraction
        amountUsed
        amountRemaining
        claimCode
        service {
          name
          id
        }
      }

      referrals(order: "reverse:createdAt", limit: 100) {
        id
        referrerRewarded
        rewardAmount
        recipient {
          fullName
          email
        }
      }
    }
  }
  ${FRAGMENTS.BaseUser}
  ${FRAGMENTS.MarketFields}
`

export const LOAD_MARKETS = gql`
  query {
    markets {
      ...MarketFields
    }
  }
  ${FRAGMENTS.MarketFields}
`

export const LOAD_MARKET_SERVICES_WITH_ADD_ONS = gql`
  query (
    $marketServicesWhere: SequelizeJSON
    $addOnMarketServicesWhere: SequelizeJSON
  ) {
    marketServices(where: $marketServicesWhere) {
      id
      isPopular
      marketId
      price
      duration
      isInformational
      informationalUrl
      informationalPriceText
      serviceId
      displayPrice
      addOnMarketServices(where: $addOnMarketServicesWhere) {
        id
        addOnPrice
        displayPrice
        addOnDuration
        serviceId
      }
    }
  }
`

export const FETCH_MARKET = gql`
  query ($id: String!) {
    market(id: $id) {
      ...MarketFields
    }
  }
  ${FRAGMENTS.MarketFields}
`

export const APPOINTMENT_SERVICES = gql`
  query ($where: SequelizeJSON, $limit: Int, $offset: Int) {
    appointmentServices(
      where: $where
      limit: $limit
      offset: $offset
      order: "reverse:startDateTime"
    ) {
      id
      providerPositions
      provider {
        id
      }
      service {
        id
        name
        serviceType {
          id
        }
      }
      appointment {
        id
        startDateTime
        owner {
          id
        }
        address {
          latitude
          longituded
          googlePlaceId
          market {
            id
            name
            timezone
          }
        }
      }
    }
  }
`

export const SERVICE_TYPES = gql`
  query {
    serviceTypes {
      id
      name
      priority
    }
  }
`

export const LOAD_SERVICES = gql`
  query ($where: SequelizeJSON) {
    services(where: $where) {
      id

      name
      description
      price
      duration

      addOnName
      addOnDescription
      addOnPrice
      addOnDuration

      isAddOn
      isPureAddOn
      isCombined
      combinedAddOnServiceCompositeId
      addOnName
      addOnDescription
      addOnPrice
      addOnDuration
      maxPerAppointment
      backToBackOnly
      firstTimeBooking
      image
      thumbnailImage
      instructionalImage
      infoHowToPrep
      infoWhatsIncluded
      infoTagline
      thingsToKnow
      baseResourceUrl
      priority

      isInformational
      informationalUrl
      informationalPriceText
      serviceTypeId
    }
  }
`

export const PRODUCTS = gql`
  query ($where: SequelizeJSON) {
    products(where: $where, order: "name") {
      id
      name
      sku
      category
      price
      isMembership
      renewalPrice
      creditAmount
      serviceType {
        name
      }
    }
  }
`

export const ADDRESSES = gql`
  query ($where: SequelizeJSON) {
    addresses(where: $where) {
      id
      street
      apartment
      city
      state
      zip
      name
    }
  }
`

export const CREATE_ADDRESS = gql`
  mutation ($address: AddressInput!) {
    createAddress(address: $address) {
      id
      userId
      street
      apartment
      city
      state
      zip
      type
      notes
      pets
      latitude
      longitude
      marketId
    }
  }
`

export const UPDATE_ADDRESS = gql`
  mutation ($id: String!, $address: AddressInput!) {
    updateAddress(id: $id, address: $address) {
      id
      userId
      street
      apartment
      city
      state
      zip
      type
      notes
      pets
      latitude
      longitude
      marketId
    }
  }
`

export const UPDATE_APPOINTMENT = gql`
  mutation ($id: String!, $appointment: AppointmentInput!) {
    updateAppointment(id: $id, appointment: $appointment) {
      id
      notes
      actualStartDateTime
      actualEndDateTime
    }
  }
`

export const UPDATE_APPOINTMENT_SERVICE = gql`
  mutation ($id: String!, $appointmentService: AppointmentServiceInput!) {
    updateAppointmentService(id: $id, appointmentService: $appointmentService) {
      id
      duration
      price
      customPrice
      baseService {
        id
        name
        serviceType {
          id
          name
        }
      }
      addOnServices {
        id
        name
        price
        description
      }
      provider {
        photoUrls
        nameFirst
        nameLast
      }
    }
  }
`
export const DESTROY_APPOINTMENT_SERVICE = gql`
  mutation ($id: String!) {
    destroyAppointmentService(id: $id) {
      id
    }
  }
`

export const DESTROY_ADDRESS = gql`
  mutation ($id: String!) {
    destroyAddress(id: $id) {
      id
    }
  }
`

export const CREATE_PAYMENT_METHOD = gql`
  mutation ($paymentMethod: PaymentMethodInput!) {
    createPaymentMethod(paymentMethod: $paymentMethod) {
      id
      brand
      type
      fullName
      expirationMonth
      expirationYear
      number
    }
  }
`

export const DESTROY_PAYMENT_METHOD = gql`
  mutation ($id: String!) {
    destroyPaymentMethod(id: $id) {
      id
    }
  }
`

export const CREATE_PAYMENT_INTENT = gql`
  mutation ($amount: Float!) {
    createPaymentIntent(amount: $amount) {
      id
      clientSecret
    }
  }
`

export const CREATE_USER_CREDIT = gql`
  mutation ($userCredit: UserCreditInput!) {
    createUserCredit(userCredit: $userCredit) {
      userId
      claimCode
      amount
      validUntil
    }
  }
`

export const PAYMENT_METHODS = gql`
  query ($where: SequelizeJSON) {
    paymentMethods(where: $where) {
      id
      brand
      type
      number
    }
  }
`

export const SHIPPING_RATES = gql`
  query {
    shippingRates {
      id
      name
      price
    }
  }
`

export const AT_RISK_APPOINTMENT_SERVICES = gql`
  query ($where: SequelizeJSON) {
    appointmentServices(where: $where) {
      id
      lateRiskLabel
      lateRiskScore
      lateRiskResolved
    }
  }
`

export const OWNER_API_REQUESTS = gql`
  query ($where: SequelizeJSON, $limit: Int, $offset: Int, $order: String) {
    apiRequests(where: $where, limit: $limit, offset: $offset, order: $order) {
      id
      createdAt
      method
      originalUrl
      bodyUrl
      bodyKey
      statusCode
      gqlQueryInformation
      responseTime
      clientApplication {
        description
      }
    }
  }
`

export const OWNER_COMMUNICATION_LOG = gql`
  query ($where: SequelizeJSON, $limit: Int, $offset: Int, $order: String) {
    communicationLogs(
      where: $where
      limit: $limit
      offset: $offset
      order: $order
    ) {
      id
      createdAt

      type

      subject
      body
      html

      error
      isErrored
      userId

      externalChannel
      externalId
      externalCampaignId
      externalAddress

      appointmentId
      appointmentServiceId

      appointmentService {
        id
        appointmentId
        startDateTime
        baseService {
          name
        }
        addOnServices {
          name
        }
        appointment {
          id
          actualStartDateTime
          address {
            market {
              timezone
            }
          }
        }
      }
    }
  }
`

export const OWNER_LOG_INS = gql`
  query ($where: SequelizeJSON, $limit: Int, $offset: Int, $order: String) {
    ownerLogins: accessTokens(
      where: $where
      limit: $limit
      offset: $offset
      order: $order
    ) {
      createdAt
      userAgent
      clientApplication {
        shortName
        description
      }
    }
  }
`

export const AT_RISK_APPOINTMENT_SERVICE_SUBSCRIPTION = gql`
  subscription ($filter: JSON) {
    appointmentServiceEvent(filter: $filter) {
      appointmentService {
        id
        lateRiskLabel
        lateRiskScore
        lateRiskResolved
      }
    }
  }
`

export const OWNER_EMAILS = gql`
  query ($where: SequelizeJSON, $limit: Int, $offset: Int, $order: String) {
    emails(where: $where, limit: $limit, offset: $offset, order: $order) {
      id
      createdAt
      subject
      sentAt
    }
  }
`

export const OWNER_APPOINTMENTS = gql`
  query ($where: SequelizeJSON, $limit: Int, $offset: Int, $order: String) {
    appointments(where: $where, limit: $limit, offset: $offset, order: $order) {
      id
      createdAt
      startDateTime
      isCanceled
      isPaymentReceived
      ownerId
      address {
        market {
          timezone
        }
      }
      appointmentServices {
        isProviderLocked
        baseService {
          name
        }
        addOnServices {
          name
        }
        provider {
          fullName
        }
      }
    }
  }
`

export const APPOINTMENT_DETAILS = gql`
  query ($appointmentId: String!) {
    appointment(id: $appointmentId) {
      id
      tipRate
      willTipInCash
      totalPrice
      isPaymentReceived
      owner {
        id
        email
        fullName
      }
      address {
        street
        city
        state
        zip
        market {
          fullyAdjustableTipRate
          handlingFee
        }
      }
      actualStartDateTime
      appointmentServices {
        ...AppointmentServiceAttributes
      }
      surveyAnswers {
        __typename
        id
        value
        valueText
        question {
          __typename
          id
          value
          questionGroup {
            __typename
            name
            by
          }
        }
      }
    }
  }
  ${FRAGMENTS.AppointmentServiceAttributes}
`

export const APPOINTMENT_SERVICE_DETAILS = gql`
  query ($appointmentServiceId: String!) {
    appointmentService(id: $appointmentServiceId) {
      ...AppointmentServiceAttributes
    }
  }
  ${FRAGMENTS.AppointmentServiceAttributes}
`

export const CREATE_APPOINTMENT = gql`
  mutation ($appointment: AppointmentInput!) {
    createAppointment(appointment: $appointment) {
      address {
        street
        city
        state
        zip
      }
      actualStartDateTime
      totalPrice
      finalPrice
    }
  }
`

export const CREATE_SURVEY_ANSWER = gql`
  mutation ($surveyAnswer: SurveyAnswerInput!) {
    createSurveyAnswer(surveyAnswer: $surveyAnswer) {
      id
      appointmentId
      surveyQuestionId
      value
      appointmentServiceId
      resultingUserCredit {
        amount
      }
    }
  }
`

export const UPDATE_SURVEY_ANSWER = gql`
  mutation ($id: String!, $surveyAnswer: SurveyAnswerInput!) {
    updateSurveyAnswer(id: $id, surveyAnswer: $surveyAnswer) {
      id
      appointmentId
      surveyQuestionId
      value
      appointmentServiceId
      resultingUserCredit {
        amount
      }
    }
  }
`

export const UPDATE_MEMBERSHIP = gql`
  mutation ($id: String!, $membership: MembershipInput!) {
    updateMembership(id: $id, membership: $membership) {
      id
    }
  }
`

export const LOAD_MEMBERSHIPS = gql`
  query {
    products(
      where: { isMembership: true, isEnabled: true }
      order: "priority"
    ) {
      id
      name
      description
      price
      discountLabel
      priority
      renewalPrice
      renewalPeriod
      prepaidRenewalPeriods

      markets {
        id
      }
    }
  }
`

export const LOAD_PACKAGES = gql`
  query ($marketId: String!) {
    market(id: $marketId) {
      name
      id
      products(
        where: {
          isMembership: false
          isPhysicalProduct: false
          isEnabled: true
        }
        order: "priority"
      ) {
        id
        sku
        name
        description
        price
        discountLabel
        priority
        creditAmount
        productGroup {
          id
          name
          isEnabled
          priority
        }
      }
    }
  }
`

export const LOAD_USER_APPOINTMENTS = gql`
  query {
    me {
      id
      nameFirst
      appointments(where: { isCanceled: false }, order: "reverse:createdAt") {
        id
        actualStartDateTime
        actualEndDateTime
        isCanceled
        totalPrice
        finalPriceWithTaxAndTip
        taxAmount
        tipAmount
        creditsApplied
        handlingFee
        notes
        address {
          id
          type
          street
          apartment
          city
          state
          zip
          pets
          notes

          market {
            timezone
            serviceTax
          }
        }
        appointmentServices {
          id
          duration
          price
          customPrice
          baseService {
            name
          }
          addOnServices {
            name
          }
          provider {
            photoUrls
            nameFirst
            nameLast
          }
        }
      }
    }
  }
`

export const LOAD_APPOINTMENTS = gql`
  query ($where: SequelizeJSON, $limit: Int, $offset: Int, $order: String) {
    appointments(where: $where, limit: $limit, offset: $offset, order: $order) {
      __typename
      id
      actualStartDateTime
      actualEndDateTime
      startDateTime
      isCanceled
      totalPrice
      finalPriceWithTaxAndTip
      taxAmount
      tipAmount
      creditsApplied
      handlingFee
      notes
      ownerId
      address {
        __typename
        id
        type
        street
        apartment
        city
        state
        zip
        pets
        notes
        name
        market {
          __typename
          id
          timezone
          serviceTax
          # ...MarketFields
        }
      }
      appointmentServices {
        __typename
        id
        duration
        startDateTime
        price
        customPrice
        timezone
        providerConfirmedAt
        providerId
        isSquadSearch
        baseService {
          __typename
          id
          name
          serviceType {
            __typename
            id
            name
          }
        }
        addOnServices {
          __typename
          id
          name
          price
          description
        }
        provider {
          __typename
          photoUrls
          photoUrl
          nameFirst
          nameLast
        }
      }
    }
  }
`

export const LOAD_GIFT_CARD = gql`
  query {
    products(where: { category: "Gift Certificate", name: "Custom Amount" }) {
      id
      name
    }
  }
`

export const LOAD_SERVICES_FOR_MARKET = gql`
  query ($id: String!) {
    market(id: $id) {
      __typename
      id
      name
      marketServices(where: { isAddOn: false }) {
        __typename
        id
        price
        duration
        displayPrice
        service {
          __typename
          id
          priority
          name
          description
          isInformational
          informationalUrl
          informationalPriceText
          maxPerAppointment
          infoHowToPrep
          infoWhatsIncluded
          isPopular
          serviceTypeId
          serviceType {
            id
            name
            priority
            serviceInfoTitle
            serviceInfoBody
          }
        }

        addOnMarketServices(where: { isPureAddOn: true }) {
          __typename
          id
          displayPrice
          service {
            __typename
            id
            priority
            duration
            price
            addOnDuration
            addOnPrice
            name
            description
          }
        }
      }
    }
  }
`

export const LOAD_DETAIL_SERVICES = gql`
  query {
    services(where: { isAddOn: false }) {
      __typename
      id
      slug
      name
      description
      infoTagline
      price
      duration
      serviceDetailRating
      infoWhatsIncluded
      infoHowToPrep
      isPopular
      isCrossSell
      isInformational
      isFlatRate
      isDurationAdjustable
      mobileImageUrl
      mobileImageAttribution
      thingsToKnow
      priority
      serviceTypeId
      serviceType {
        id
        name
        priority
      }
      addOnServices {
        id
        name
        addOnName
        addOnDescription
        addOnPrice
        addOnDuration
        mobileFeaturedImage
      }
    }
  }
`

export const LOAD_CLIENT_SURVEY_QUESTIONS = gql`
  query {
    surveyQuestionGroups {
      __typename
      id
      name
      by
      for
      questions(where: { isEnabled: true }) {
        __typename
        id
        isEnabled
        type
        value
        isFloat
        priority
        questionGroup {
          __typename
          name
        }
      }
    }
  }
`

export const FETCH_PRODUCT = gql`
  query ($id: String!) {
    product(id: $id) {
      __typename
      id
      name
      discountLabel
      sku
      description
      category
      price
      isMembership
      renewalPrice
      creditAmount
      serviceType {
        name
      }
    }
  }
`

export const GET_BANNER = gql`
  query ($clientApplicationId: String!) {
    getBanner(clientApplicationId: $clientApplicationId) {
      banners {
        id
        text
        secondaryText
        link
        startDateTime
        endDateTime
        isDefault
        defaultArchivedAt
        clientApplicationId
      }
      defaultBanner {
        id
        text
        secondaryText
        link
        startDateTime
        endDateTime
        isDefault
        defaultArchivedAt
        clientApplicationId
      }
    }
  }
`

/**
 * APPOINTMENT_SERVICE_INPUT_FIELDS
 *
 * Fields a client role is allowed to send on `AppointmentServiceInput`. The server
 * recomputes price and duration from the service ids (or restores them from the signed
 * booking token), so only identity, scheduling and tip intent need to travel over the
 * wire. Anything not listed here — `price`, `duration`, `customPrice`, `additionalFees`
 * — is not defined by the input type for a client role and would fail GraphQL variable
 * coercion, killing the whole operation before any resolver runs.
 *
 * Pick against this allowlist rather than omitting known-bad fields: a blocklist has to
 * be extended every time the server restricts another field.
 *
 * One list covers every operation, because `AppointmentServiceInput` is a single type
 * shared by CALCULATE_APPOINTMENT_PRICE and UPDATE_APPOINTMENT_SERVICE. It is therefore
 * the union of what those paths send, which is wider than mantis's equivalent: the
 * post-appointment survey writes the three tip fields through this same type, so they
 * belong to it even though the pricing path never sends them. No query selects
 * `adjustedTipAmount` / `additionalTipAmount` / `didTipInCash`, so they are never
 * present on a hydrated service object and listing them cannot widen the pricing
 * payload.
 *
 * Narrowing this list silently drops writes — a mutation still succeeds, it just
 * persists nothing. Add a test alongside any change.
 */
export const APPOINTMENT_SERVICE_INPUT_FIELDS = [
  'id',
  'appointmentId',
  'serviceId',
  'baseServiceId',
  'addOnServiceIds',
  'providerId',
  'startDateTime',
  'willTipInCash',
  'bookingToken',
  // written by the post-appointment survey (pages/appointment-survey.js)
  'adjustedTipAmount',
  'additionalTipAmount',
  'didTipInCash'
]

export const CALCULATE_APPOINTMENT_PRICE = gql`
  query (
    $startDateTime: Date!
    $address: AddressInput!
    $appointmentServices: [AppointmentServiceInput!]!
    $tipRate: Float
  ) {
    calculateAppointmentPrice(
      startDateTime: $startDateTime
      address: $address
      appointmentServices: $appointmentServices
      tipRate: $tipRate
    ) {
      appointment {
        totalPrice
        creditsApplied
        paidCreditsApplied
        finalPrice
        finalPriceWithTaxAndTip
        tipAmount
        taxAmount
        handlingFee
        additionalFees
      }
    }
  }
`

export const FULL_DAY_AVAILABILITY = gql`
  query FullDayAvailability(
    $date: String!
    $timeSlotStepMinutes: Int!
    $address: AddressInput!
    $services: [ServiceRequestInput!]!
    $maxPreferredProviders: Int
  ) {
    fullDayAvailability(
      date: $date
      timeSlotStepMinutes: $timeSlotStepMinutes
      address: $address
      services: $services
      maxPreferredProviders: $maxPreferredProviders
    ) {
      startDateTime
      appointment {
        totalPrice
        bookingTokens
        appointmentServices {
          baseServiceId
          addOnServiceIds
          providerId
          providerName
          duration
          price
          bookingToken
        }
      }
      partialAppointment {
        totalPrice
        bookingTokens
        appointmentServices {
          baseServiceId
          addOnServiceIds
          providerId
          providerName
          duration
          price
          bookingToken
        }
      }
    }
  }
`
